// src/pages/BuyersPage.jsx
import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// SVG placeholder for images
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#cbd5e1"/></linearGradient></defs>
    <rect width="1200" height="800" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="600" y="390" font-size="28" font-weight="700">Property image</text>
      <text x="600" y="425" font-size="14" opacity="0.85">Not available</text>
    </g>
  </svg>`);

const fmtGBP = (v, text) =>
  text ? text : v == null ? 'POA' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);

export default function BuyersPage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  const heroImage = '/media/hero-property-poster.jpg';

  // fetch a few featured sale properties
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams({
          types: 'sale',
          status: 'active',
          per_page: '6',
          sort: 'newest',
        });
        const res = await fetch(`${API_BASE}/properties.php?${qs.toString()}`);
        const json = await res.json().catch(() => ({}));
        if (!cancel && json?.ok) setFeatured(Array.isArray(json.data) ? json.data : []);
      } catch {
        // swallow – show empty state
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const services = [
    {
      icon: '🔎',
      title: 'Tailored Property Search',
      description:
        'We shortlist homes that fit your budget, location and lifestyle — then schedule viewings to suit you.',
    },
    {
      icon: '🏦',
      title: 'Mortgage & Finance Advice',
      description:
        'Access trusted brokers to secure competitive rates and lending tailored to your situation.',
    },
    {
      icon: '🗓️',
      title: 'Viewings & Open Days',
      description:
        'Accompanied viewings with honest feedback so you can compare options confidently.',
    },
    {
      icon: '🤝',
      title: 'Offer & Negotiation',
      description:
        'From first offer to exchange, we negotiate the right terms and keep things moving.',
    },
  ];

  const steps = [
    { n: 1, t: 'Get mortgage ready', d: 'Speak to a broker for an Agreement in Principle so you can offer with confidence.' },
    { n: 2, t: 'Search & view', d: 'Tell us your wish list. We’ll line up viewings and keep you in the loop on new listings.' },
    { n: 3, t: 'Make your offer', d: 'We present your offer, qualify your position and negotiate the best outcome.' },
    { n: 4, t: 'Conveyancing', d: 'We recommend solicitors, help chase searches and keep everyone on track.' },
    { n: 5, t: 'Surveys & checks', d: 'We coordinate surveys and resolve queries promptly to avoid delays.' },
    { n: 6, t: 'Exchange & completion', d: 'Final checks, deposit transfer, keys collected. Welcome home!' },
  ];

  return (
    <>
      <style>{`
        /* ===== Buyers Page (namespaced) ===== */
        .mrm-buy { --b:#1e3a8a; --b2:#0ea5e9; --ink:#0f172a; --muted:#475569; --bg:#f8fafc; --paper:#fff; --line:#e5e7eb; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:var(--ink); }
        .mrm-buy a { color: var(--b); text-decoration: none; }
        .mrm-buy a:hover { text-decoration: underline; }
        .mrm-buy__container { max-width:1200px; margin:0 auto; padding: 1rem; }

        /* HERO */
        .mrm-buy__hero{
          position:relative; color:#fff; min-height:66vh; display:grid; align-items:center; isolation:isolate;
          background-image:
            linear-gradient(180deg, rgba(2,6,23,.25) 0%, rgba(2,6,23,.65) 100%),
            var(--hero);
          background-size:cover; background-position:center;
        }
        .mrm-buy__crumb{ opacity:.9; margin-bottom:.5rem; }
        .mrm-buy__title{ margin:.25rem 0; font-weight:900; line-height:1.05; font-size: clamp(1.8rem, 4.2vw, 3rem); text-shadow:0 2px 18px rgba(0,0,0,.25); }
        .mrm-buy__lede{ max-width: 820px; font-size: clamp(1rem, 2vw, 1.15rem); opacity:.95; }
        .mrm-buy__cta{ display:flex; gap:.8rem; flex-wrap:wrap; margin-top:1.25rem; }
        .mrm-buy__btn{
          display:inline-flex; align-items:center; justify-content:center; gap:.5rem; font-weight:900; border-radius:999px; padding:.9rem 1.2rem; cursor:pointer; border:1px solid rgba(255,255,255,.7); color:#fff; background:transparent; transition:.2s transform,.25s box-shadow,.2s background;
        }
        .mrm-buy__btn--primary{ background:#fff; border-color:#fff; color:var(--b); }
        .mrm-buy__btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.25); }
        .mrm-buy__chips{ display:flex; gap:.5rem; flex-wrap:wrap; margin-top:1rem; }
        .mrm-buy__chip{ background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.3); color:#fff; padding:.4rem .6rem; border-radius:10px; font-weight:800; font-size:.84rem; }

        /* SECTION */
        .mrm-buy__section{ padding: clamp(3rem, 6vw, 5.5rem) 0; background: var(--bg); }
        .mrm-buy__section--alt{ background:#fff; }
        .mrm-buy__heading{ margin:0; font-weight:900; font-size: clamp(1.4rem, 3vw, 2rem); color: var(--b); }
        .mrm-buy__sub{ margin:.35rem 0 1rem; color: var(--muted); max-width: 900px; }

        /* SERVICES */
        .mrm-buy__grid{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .mrm-buy__grid{ grid-template-columns:1fr 1fr; } }
        @media (min-width: 1100px){ .mrm-buy__grid{ grid-template-columns:1fr 1fr 1fr 1fr; } }
        .mrm-buy__card{ background:#fff; border:1px solid var(--line); border-radius:14px; box-shadow:0 10px 22px rgba(2,6,23,.06); padding:1rem; height:100%; display:grid; gap:.5rem; }
        .mrm-buy__icon{ width:44px; height:44px; border-radius:12px; display:grid; place-items:center; background:#dbeafe; color:#0b3aa4; font-weight:900; font-size:1.15rem; }
        .mrm-buy__card h3{ margin:.2rem 0 0; font-size:1rem; font-weight:900; }
        .mrm-buy__card p{ margin:0; color:#475569; font-size:.95rem; line-height:1.55; }

        /* STEPS */
        .mrm-buy__steps{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .mrm-buy__steps{ grid-template-columns:1fr 1fr 1fr; } }
        .mrm-buy__step{ background:#fff; border:1px solid var(--line); border-radius:14px; padding:1rem; display:flex; gap:.85rem; }
        .mrm-buy__num{ flex:0 0 auto; width:36px; height:36px; border-radius:50%; background:var(--b); color:#fff; font-weight:900; display:grid; place-items:center; }
        .mrm-buy__step h4{ margin:.1rem 0 .1rem; font-size:1rem; font-weight:900; }
        .mrm-buy__step p{ margin:0; color:#475569; font-size:.95rem; }

        /* MORTGAGE CTA */
        .mrm-buy__band{ color:#fff; background: linear-gradient(135deg, var(--b), var(--b2)); border-radius:20px; padding: clamp(1.2rem, 4vw, 2rem); }
        .mrm-buy__band h3{ margin:.2rem 0; font-size: clamp(1.2rem, 2.4vw, 1.6rem); font-weight:900; }
        .mrm-buy__band p{ margin:.2rem 0 1rem; opacity:.95; }

        /* FEATURED PROPERTIES */
        .mrm-buy__props{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width:760px){ .mrm-buy__props{ grid-template-columns:1fr 1fr; } }
        @media (min-width:1100px){ .mrm-buy__props{ grid-template-columns:1fr 1fr 1fr; } }
        .mrm-buy__prop{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; display:flex; flex-direction:column; }
        .mrm-buy__thumb{ position:relative; height:180px; background:#e5e7eb; }
        .mrm-buy__thumb img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-buy__pill{ position:absolute; top:10px; left:10px; background:rgba(0,0,0,.6); color:#fff; border-radius:999px; padding:.25rem .5rem; font-size:.75rem; font-weight:800; }
        .mrm-buy__body{ padding:.8rem .9rem; display:grid; gap:.35rem; }
        .mrm-buy__h{ margin:0; font-weight:800; font-size:1rem; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:2.6em; }
        .mrm-buy__meta{ color:#64748b; font-size:.9rem; }
        .mrm-buy__price{ color:#b91c1c; font-weight:900; }
        .mrm-buy__specs{ color:#475569; font-size:.9rem; }

        /* FAQ */
        .mrm-buy__faq{ display:grid; grid-template-columns:1fr; gap:.7rem; }
        @media (min-width: 900px){ .mrm-buy__faq{ grid-template-columns:1fr 1fr; } }
        .mrm-buy details{ background:#fff; border:1px solid var(--line); border-radius:12px; padding:.7rem 1rem; }
        .mrm-buy summary{ cursor:pointer; font-weight:900; list-style:none; }
        .mrm-buy summary::-webkit-details-marker{ display:none; }

        /* FINAL CTA */
        .mrm-buy__final{ background:#fff; border:1px solid var(--line); border-radius:18px; padding: clamp(1.2rem, 4vw, 2rem); display:grid; grid-template-columns:1fr; gap:1rem; align-items:center; }
        @media (min-width:900px){ .mrm-buy__final{ grid-template-columns:2fr 1fr; } }

        /* Utility */
        .mrm-buy__row{ display:flex; gap:.8rem; flex-wrap:wrap; align-items:center; }
        .mrm-buy__btn--outline{ background:transparent; color:#fff; border:1px solid rgba(255,255,255,.7); }
        .mrm-buy__btn--ghost{ background:transparent; color:var(--b); border:1px solid var(--line); }
        .mrm-buy__muted{ color:#64748b; font-size:.9rem; }
        .mrm-buy__divider{ height:1px; background: var(--line); margin: 1rem 0; }

        /* Back to top */
        .mrm-buy__top{ position:fixed; right:18px; bottom:18px; border:none; background:var(--b); color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; box-shadow:0 10px 22px rgba(2,6,23,.25); font-weight:900; }
        .mrm-buy__top:hover{ transform: translateY(-1px); }
      `}</style>

      <main className="mrm-buy">
        {/* HERO */}
        <header className="mrm-buy__hero" style={{ '--hero': `url('${heroImage}')` }}>
          <div className="mrm-buy__container">
            <div className="mrm-buy__crumb">
              <a href="/">Home</a> &rsaquo; Buyers
            </div>
            <h1 className="mrm-buy__title">
              Find the one. <span style={{ color: '#c8d5f3' }}>We’ll guide you every step.</span>
            </h1>
            <p className="mrm-buy__lede">
              One dedicated Middlesbrough office with practical local knowledge.
              Search available homes and move forward with clear guidance.
            </p>
            <div className="mrm-buy__cta">
              <a href="/properties?types=sale" className="mrm-buy__btn mrm-buy__btn--primary">Browse homes for sale →</a>
              <a href="/contact" className="mrm-buy__btn mrm-buy__btn--outline">Speak to an advisor</a>
            </div>
          </div>
        </header>

        {/* SERVICES */}
        <section className="mrm-buy__section">
          <div className="mrm-buy__container">
            <h2 className="mrm-buy__heading">How we help you buy</h2>
            <p className="mrm-buy__sub">From first viewing to handing over the keys, we’ll simplify the process and keep you informed.</p>

            <div className="mrm-buy__grid">
              {services.map((s) => (
                <article key={s.title} className="mrm-buy__card">
                  <div className="mrm-buy__icon" aria-hidden="true">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section className="mrm-buy__section mrm-buy__section--alt">
          <div className="mrm-buy__container">
            <h2 className="mrm-buy__heading">Your buying journey</h2>
            <p className="mrm-buy__sub">A clear, six-step path from search to completion.</p>

            <div className="mrm-buy__steps">
              {steps.map((s) => (
                <article key={s.n} className="mrm-buy__step">
                  <div className="mrm-buy__num">{s.n}</div>
                  <div>
                    <h4>{s.t}</h4>
                    <p>{s.d}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mrm-buy__row" style={{ marginTop: '1rem' }}>
              <a href="/contact" className="mrm-buy__btn mrm-buy__btn--ghost">Ask a buying question</a>
              <a href="/contact" className="mrm-buy__btn mrm-buy__btn--ghost">Ask a question →</a>
            </div>
          </div>
        </section>

        {/* MORTGAGE CTA */}
        <section className="mrm-buy__section">
          <div className="mrm-buy__container">
            <div className="mrm-buy__band">
              <h3>Get mortgage-ready with expert advice</h3>
              <p>We’ll introduce you to trusted brokers who can provide an Agreement in Principle and help you secure a competitive product.</p>
              <div className="mrm-buy__row">
                <a href="/contact" className="mrm-buy__btn mrm-buy__btn--primary">Speak to our office</a>
                <a href="/contact" className="mrm-buy__btn mrm-buy__btn--outline">Contact us</a>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED PROPERTIES (from API) */}
        <section className="mrm-buy__section mrm-buy__section--alt" aria-live="polite">
          <div className="mrm-buy__container">
            <h2 className="mrm-buy__heading">Featured properties</h2>
            <p className="mrm-buy__sub">A selection of the latest homes for sale across Teesside.</p>

            {!loading && featured.length === 0 && (
              <div className="mrm-buy__muted">No properties to show right now. Try again later or browse all listings.</div>
            )}

            {loading ? (
              <div className="mrm-buy__props">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="mrm-buy__prop" aria-busy="true">
                    <div className="mrm-buy__thumb" style={{ background: 'linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 37%,#e5e7eb 63%)', backgroundSize: '900px 100%', animation: 'shimmer 1.2s infinite' }} />
                    <div className="mrm-buy__body">
                      <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '6px', width: '80%' }} />
                      <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '6px', width: '60%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mrm-buy__props">
                {featured.map((p) => {
                  const img = p.full_image_url || p.image_url || PLACEHOLDER;
                  const href = p.href || (p.slug ? `/property/${p.slug}` : '#');
                  const meta = [p.town, p.postcode].filter(Boolean).join(' · ');
                  return (
                    <a key={p.id} className="mrm-buy__prop" href={href}>
                      <div className="mrm-buy__thumb">
                        <img
                          src={img}
                          alt={p.title}
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER;
                          }}
                        />
                        {p.listing_type && <span className="mrm-buy__pill">{String(p.listing_type).toUpperCase()}</span>}
                      </div>
                      <div className="mrm-buy__body">
                        <h3 className="mrm-buy__h">{p.title}</h3>
                        <div className="mrm-buy__meta">{meta}</div>
                        <div className="mrm-buy__price">{fmtGBP(p.price, p.price_text)}</div>
                        <div className="mrm-buy__specs">🛏 {p.beds ?? 0} · 🛁 {p.baths ?? 0}</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            <div className="mrm-buy__row" style={{ marginTop: '1rem' }}>
              <a href="/properties?types=sale" className="mrm-buy__btn mrm-buy__btn--ghost">View all properties →</a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mrm-buy__section">
          <div className="mrm-buy__container">
            <h2 className="mrm-buy__heading">Buyer FAQs</h2>
            <div className="mrm-buy__sub">Answers to common questions — and we’re just a call away if you’d like to talk.</div>

            <div className="mrm-buy__faq">
              {[
                ['Do I need a mortgage in principle before viewing?', 'It isn’t mandatory, but it strengthens your position and speeds up offer verification.'],
                ['How quickly can I complete?', 'Typical timelines range 8–12 weeks depending on the chain, searches and your lender.'],
                ['Can you recommend a solicitor?', 'Yes — we work with local conveyancers who provide clear updates and competitive fees.'],
                ['What if I’m a first-time buyer?', 'Perfect — we’ll explain each step plainly and support you from viewing to keys.'],
              ].map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <div style={{ marginTop: '.4rem', color: '#475569' }}>{a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mrm-buy__section mrm-buy__section--alt">
          <div className="mrm-buy__container">
            <div className="mrm-buy__final">
              <div>
                <h3 className="mrm-buy__heading" style={{ marginBottom: '.25rem' }}>Ready to start your search?</h3>
                <p className="mrm-buy__sub" style={{ marginTop: 0 }}>
                  Register for priority alerts and browse the latest homes for sale across Teesside.
                </p>
              </div>
              <div className="mrm-buy__row" style={{ justifyContent: 'flex-end' }}>
                <a href="/#property-alerts" className="mrm-buy__btn" style={{ background:'#1e3a8a', color:'#fff', borderColor:'#1e3a8a' }}>Register for alerts</a>
                <a href="/properties?types=sale" className="mrm-buy__btn mrm-buy__btn--ghost">View properties</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Back to top */}
      <button
        className="mrm-buy__top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>

      {/* skeleton keyframes */}
      <style>{`
        @keyframes shimmer { 0%{background-position:-450px 0} 100%{background-position:450px 0} }
      `}</style>
    </>
  );
}
